import InstagramPostModal from '@/components/admin/InstagramPostModal'
import { Area, Field, Panel, UploadHint } from '@/components/admin/Field'
import { generateInstagramPost } from '@/lib/instagramPost'
import { articlePath, detectCategory, makeExcerpt, slugify } from '@/lib/news'
import { lumi } from '@/lib/lumi'
import { uploadNewsImage } from '@/lib/upload'
import type { NewsArticle } from '@/types/news'
import {ImagePlus, Instagram, Link2, Trash2} from 'lucide-react'
import { FormEvent, useMemo, useState } from 'react'
import toast from 'react-hot-toast'

function nowIso() {
  return new Date().toISOString()
}

function ownsArticle(article: Pick<NewsArticle, 'author' | 'creator'>, username: string) {
  const name = (username || '').trim().toLowerCase()
  if (!name) return false
  return (article.author || '').trim().toLowerCase() === name || (article.creator || '').trim().toLowerCase() === name
}

interface NewsManagerProps {
  articles: NewsArticle[]
  authorName: string
  isAuthor?: boolean
  onSaved: () => Promise<void>
}

function NewsManager({ articles, authorName, isAuthor = false, onSaved }: NewsManagerProps) {
  const empty: Partial<NewsArticle> = {
    title: '',
    body: '',
    excerpt: '',
    featuredImage: '',
    gallery: [],
    published: true,
  }
  const [form, setForm] = useState<Partial<NewsArticle>>(empty)
  const [editingId, setEditingId] = useState('')
  const [saving, setSaving] = useState(false)
  const [instagramBusyId, setInstagramBusyId] = useState('')
  const [instagramPreview, setInstagramPreview] = useState<{ url: string; title: string; filename: string } | null>(null)

  const category = useMemo(
    () => detectCategory(form.title || '', form.body || ''),
    [form.title, form.body],
  )
  const slug = slugify(form.title || '')
  const sharePath = articlePath(slug)
  const shareUrl = typeof window !== 'undefined' ? `${window.location.origin}${sharePath}` : sharePath

  const canManage = (item: NewsArticle) => !isAuthor || ownsArticle(item, authorName)

  const onFeatured = async (file?: File) => {
    if (!file) return
    try {
      const url = await uploadNewsImage(file)
      setForm((prev) => ({ ...prev, featuredImage: url }))
      toast.success('Imagem destacada enviada.')
    } catch (err) {
      console.error(err)
      toast.error(err instanceof Error ? err.message : 'Falha no envio da imagem.')
    }
  }

  const onGallery = async (files?: FileList | null) => {
    if (!files?.length) return
    try {
      const uploaded = await Promise.all(Array.from(files).slice(0, 8).map((file) => uploadNewsImage(file)))
      setForm((prev) => ({ ...prev, gallery: [...(prev.gallery || []), ...uploaded] }))
      toast.success('Galeria atualizada.')
    } catch (err) {
      console.error(err)
      toast.error(err instanceof Error ? err.message : 'Falha no envio da galeria.')
    }
  }

  const save = async (event: FormEvent) => {
    event.preventDefault()
    if (!form.title?.trim() || !form.body?.trim()) {
      toast.error('Preencha título e corpo da matéria.')
      return
    }
    if (editingId) {
      const current = articles.find((item) => item._id === editingId)
      if (current && !canManage(current)) {
        toast.error('Só pode editar as suas próprias matérias.')
        return
      }
    }
    setSaving(true)
    try {
      const payload = {
        title: form.title.trim(),
        slug,
        excerpt: form.excerpt?.trim() || makeExcerpt(form.body || ''),
        body: form.body.trim(),
        featuredImage: form.featuredImage || '',
        gallery: form.gallery || [],
        category,
        author: editingId ? form.author || authorName || 'Admin' : authorName || 'Admin',
        published: form.published !== false,
        shareUrl: sharePath,
        updatedAt: nowIso(),
      }
      if (editingId) {
        await lumi.entities.news.update(editingId, payload)
      } else {
        await lumi.entities.news.create({
          ...payload,
          creator: authorName || 'admin',
          createdAt: nowIso(),
        })
      }
      toast.success('Matéria guardada.')
      setForm(empty)
      setEditingId('')
      await onSaved()
    } catch (err) {
      console.error(err)
      toast.error('Não foi possível guardar a matéria.')
    } finally {
      setSaving(false)
    }
  }

  const remove = async (item: NewsArticle) => {
    if (!item._id) return
    if (!canManage(item)) {
      toast.error('Só pode eliminar as suas próprias matérias.')
      return
    }
    try {
      await lumi.entities.news.delete(item._id)
      toast.success('Matéria removida.')
      if (editingId === item._id) {
        setForm(empty)
        setEditingId('')
      }
      await onSaved()
    } catch (err) {
      console.error(err)
      toast.error('Não foi possível remover.')
    }
  }

  const makeInstagramPost = async (item: NewsArticle) => {
    if (!item.featuredImage) {
      toast.error('Esta matéria precisa de imagem destacada para o post.')
      return
    }
    const busyKey = item._id || item.slug || item.title
    setInstagramBusyId(busyKey)
    const toastId = toast.loading('A gerar o post do Instagram...')
    try {
      const url = await generateInstagramPost(item)
      if (!url) throw new Error('Não foi possível gerar o post.')
      setInstagramPreview({
        url,
        title: item.title,
        filename: `instagram-${slugify(item.title || 'materia')}.jpg`,
      })
      toast.success('Post pronto. Descarregue o PNG e publique no Instagram.', { id: toastId })
    } catch (err) {
      console.error(err)
      const message =
        err && typeof err === 'object' && 'data' in err
          ? String((err as { data?: { error?: string } }).data?.error || '')
          : ''
      toast.error(
        message || (err instanceof Error ? err.message : 'Não foi possível gerar o post.'),
        { id: toastId },
      )
    } finally {
      setInstagramBusyId('')
    }
  }

  const copyShare = async (path: string) => {
    const url = `${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`
    try {
      await navigator.clipboard.writeText(url)
      toast.success('Link de partilha copiado.')
    } catch {
      window.prompt('Copie o link:', url)
    }
  }

  return (
    <div className="grid gap-5 xl:grid-cols-[1.1fr_0.9fr]">
      <Panel title={editingId ? 'Editar matéria' : 'Criar matéria'}>
        {isAuthor ? (
          <p className="mb-3 rounded-xl bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-800">
            Como autor, só consegue editar ou eliminar as matérias publicadas com o seu nome de utilizador.
          </p>
        ) : null}
        <form onSubmit={save} className="grid gap-3">
          <Field
            label="Título"
            value={form.title || ''}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
          />
          <Area
            label="Corpo da matéria"
            rows={14}
            value={form.body || ''}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
          />
          <Area
            label="Resumo (opcional)"
            rows={2}
            value={form.excerpt || ''}
            onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
          />
          <div className="grid gap-3 rounded-2xl bg-slate-50 p-3 sm:grid-cols-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Categoria automática</p>
              <p className="mt-1 text-sm font-extrabold text-[#0b2ea8]">{category}</p>
              <p className="mt-1 text-xs text-slate-500">Detectada pelo título e pelo texto (política, educação, trânsito, etc.).</p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Autor</p>
              <p className="mt-1 text-sm font-extrabold">{editingId ? form.author || authorName : authorName || 'Admin'}</p>
              <p className="mt-1 text-xs text-slate-500">Nome de utilizador da pessoa logada, usado como Autor da matéria.</p>
            </div>
          </div>
          <label className="block">
            <span className="mb-1.5 inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
              <ImagePlus size={16} /> Imagem destacada
            </span>
            <input type="file" accept="image/*" className="w-full text-sm" onChange={(e) => void onFeatured(e.target.files?.[0])} />
            <UploadHint size="1200 × 630 px" ratio="16:9" tip="Esta imagem aparece no cartão e no preview do WhatsApp." />
            {form.featuredImage ? <img src={form.featuredImage} alt="" className="mt-2 aspect-video w-full rounded-xl object-cover" /> : null}
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-semibold text-slate-700">Galeria de imagens</span>
            <input type="file" accept="image/*" multiple className="w-full text-sm" onChange={(e) => void onGallery(e.target.files)} />
            {(form.gallery || []).length ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {form.gallery?.map((url) => (
                  <button
                    key={url}
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, gallery: (prev.gallery || []).filter((item) => item !== url) }))}
                    className="relative"
                    title="Remover da galeria"
                  >
                    <img src={url} alt="" className="h-16 w-16 rounded-lg object-cover" />
                  </button>
                ))}
              </div>
            ) : null}
          </label>
          <label className="inline-flex items-center gap-2 text-sm font-semibold">
            <input
              type="checkbox"
              checked={form.published !== false}
              onChange={(e) => setForm({ ...form, published: e.target.checked })}
            />
            Publicar no site
          </label>
          {form.title ? (
            <div className="rounded-xl border border-slate-200 p-3 text-xs text-slate-600">
              <p className="font-bold text-slate-800">Link para partilhar</p>
              <p className="mt-1 break-all">{shareUrl}</p>
              <p className="mt-1">No WhatsApp, o preview usa a imagem destacada e o título da matéria.</p>
            </div>
          ) : null}
          <button disabled={saving} className="w-full rounded-full bg-[#0b2ea8] py-3 font-bold text-white disabled:opacity-60 sm:w-auto sm:px-8">
            {saving ? 'A guardar...' : 'Guardar matéria'}
          </button>
        </form>
      </Panel>
      <Panel title="Matérias publicadas">
        <div className="grid gap-3 sm:grid-cols-2">
          {articles.map((item) => {
            const mine = canManage(item)
            const itemKey = item._id || item.slug || item.title
            const snippetSource = (item.excerpt || item.body || '').replace(/\s+/g, ' ').trim()
            const snippet = snippetSource.length > 90 ? `${snippetSource.slice(0, 90)}...` : snippetSource
            return (
              <article key={item._id} className="flex gap-3 rounded-xl border border-slate-200 p-3">
                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-slate-100">
                  {item.featuredImage ? <img src={item.featuredImage} alt="" className="h-full w-full object-cover" /> : null}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[9px] font-extrabold uppercase tracking-wide text-[#2a63e8]">{item.category}</p>
                  <p className="truncate text-sm font-bold leading-tight">{item.title}</p>
                  {snippet ? <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-slate-500">{snippet}</p> : null}
                  <p className="mt-1 text-[10px] font-semibold text-slate-400">
                    Por {item.author || 'Admin'} · {item.published ? 'Publicada' : 'Rascunho'}
                  </p>
                  {!mine ? <p className="mt-0.5 text-[10px] font-semibold text-amber-700">Só leitura.</p> : null}
                  <div className="mt-1.5 flex flex-wrap gap-2.5">
                    {mine ? (
                      <button
                        type="button"
                        className="text-xs font-bold text-[#0b2ea8]"
                        onClick={() => {
                          setForm(item)
                          setEditingId(item._id || '')
                        }}
                      >
                        Editar
                      </button>
                    ) : null}
                    <button type="button" className="inline-flex items-center gap-1 text-xs font-bold text-slate-600" onClick={() => void copyShare(item.shareUrl || articlePath(item.slug))}>
                      <Link2 size={12} /> Link
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-xs font-bold text-[#c13584] disabled:opacity-50"
                      disabled={Boolean(instagramBusyId)}
                      onClick={() => void makeInstagramPost(item)}
                    >
                      <Instagram size={12} /> {instagramBusyId === itemKey ? 'A gerar...' : 'Instagram'}
                    </button>
                    {mine ? (
                      <button type="button" onClick={() => void remove(item)} className="text-red-500">
                        <Trash2 size={14} />
                      </button>
                    ) : null}
                  </div>
                </div>
              </article>
            )
          })}
          {!articles.length ? <p className="text-sm text-slate-500">Ainda não há matérias.</p> : null}
        </div>
      </Panel>
      {instagramPreview ? (
        <InstagramPostModal
          previewUrl={instagramPreview.url}
          title={instagramPreview.title}
          filename={instagramPreview.filename}
          onClose={() => setInstagramPreview(null)}
        />
      ) : null}
    </div>
  )
}

export default NewsManager
